import type { DraggableCanvasCardData } from "@/dnd/ids";
import type { RailRegisteredResource } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import type { Canvas } from "@cohesive/api-client";

/**
 * Build a `canvas-card` drag payload for a workspaces-rail row.
 *
 * Homogeneous multi-drag includes every selected canvas only when they all
 * share the dragged row's workspace. Mixed kinds or cross-workspace
 * selections (Starred, multiple workspace sections) fail closed to the
 * single dragged canvas — a lone `sourceWorkspaceId` cannot safely stamp
 * folder drops for IDs from other workspaces.
 */
export function buildWorkspacesRailCanvasDragPayload(
  canvas: Canvas,
  selectedResources: RailRegisteredResource[],
  options: { rowSelected: boolean },
): DraggableCanvasCardData {
  const label = canvas.name || "Untitled";
  const sourceWorkspaceId = canvas.workspace_id ?? "";
  const selectedCanvases = selectedResources.filter((r) => r.kind === "canvas");
  const inMulti = options.rowSelected && selectedCanvases.length > 1;
  const allSameWorkspaceCanvas =
    inMulti &&
    selectedResources.every((r) => r.kind === "canvas") &&
    selectedCanvases.every(
      (r) => (r.data.workspace_id ?? "") === sourceWorkspaceId,
    );
  const canvases = allSameWorkspaceCanvas
    ? selectedCanvases.map((r) => r.data)
    : [canvas];

  return {
    kind: "canvas-card",
    canvasIds: canvases.map((c) => c.id),
    sourceWorkspaceId: canvases[0]?.workspace_id ?? sourceWorkspaceId,
    // Folder provenance is single-canvas only (see DraggableCanvasCardData) —
    // multi-drag must leave it undefined so root drops still un-folder.
    sourceFolderId:
      canvases.length === 1 ? (canvases[0]?.folder_id ?? null) : undefined,
    primaryName: canvases.length === 1 ? label : `${canvases.length} canvases`,
  };
}
